import { useRef } from 'react'

import { MessageParam } from '@anthropic-ai/sdk/resources'
import { useTranslation } from 'react-i18next'

import { showToast } from '@/Components/Toast'
import { ToastMessageType } from '@/Components/Toast/const'
import { askAi } from '@/utils/ai/aiBackendConnector'
import './businessCardOcr.scss'

/**
 * Props for BusinessCardOcr component
 */
export interface BusinessCardOcrProps {
  children: React.ReactNode
  tooltip?: string
  tooltipPosition?: 'top' | 'bottom' | 'left' | 'right' | 'topleft'
  onValidResult?: (data: Record<string, string>) => void
  onStart?: () => void
  onError?: (error: string) => void
}

function getOCRPromptText(): string {
  const prompt = `Extract all contact information from this business card image and return it as a valid JSON object with the following fields:
- givenName: First/given name
- familyName: Last/family/surname
- name: Full name
- title: Professional title or honorific (e.g., Dr., Prof.)
- company: Company/organization name
- position: Job title/role
- email: Email address
- email2: Secondary email address
- email3: Tertiary email address
- phone: Phone number (preserve formatting)
- phoneMobile: Mobile phone number (preserve formatting)
- phonePrivate: Private phone number (preserve formatting)
- fax: Fax number (preserve formatting)
- website: Website URL
- address: Full mailing address

Instructions:
- Use null for any fields that are not present on the card
- Preserve original formatting for phone numbers and addresses
- Ensure the output is valid, parsable JSON
- Do not include any explanatory text, only return the JSON object`
  return prompt
}

// Helper function for translation with fallback
function translate(
  t: ((key: string) => string) | undefined,
  key: string,
  fallback: string,
): string {
  return t ? t(key) : fallback
}

// Helper function for consistent error toasting
function showOcrError(
  t: ((key: string) => string) | undefined,
  messageKey: string,
  fallbackMessage: string,
) {
  showToast(
    ToastMessageType.ERROR,
    translate(t, 'components.ocr.titles.ocrError', 'OCR Error'),
    translate(t, messageKey, fallbackMessage),
  )
}

export async function ocrCard(
  imageBase64Url: string,
  t?: (key: string) => string,
) {
  let messageContent = ''
  const messages = getAiMessageContent(imageBase64Url, t)

  try {
    messageContent = await askAi(messages as Array<MessageParam>)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    showOcrError(
      t,
      'components.ocr.errors.aiRequestFailed',
      'Failed to request AI service',
    )
    return null
  }

  const jsonMatch = messageContent.match(/({[\s\S]*})/)
  const contactInformation = jsonMatch ? JSON.parse(jsonMatch[0]) : null

  if (!contactInformation) {
    showOcrError(
      t,
      'components.ocr.errors.noContactInformation',
      'No contact information found in the message content',
    )
    return null
  }

  // Check if all values in the JSON are null
  const allValuesNull = Object.values(contactInformation).every(
    (value) => value === null || value === undefined || value === '',
  )

  if (allValuesNull) {
    showOcrError(
      t,
      'components.ocr.errors.extractionFailed',
      'No contact information could be extracted from the business card image',
    )
    return null
  }

  return contactInformation
}

function getAiMessageContent(
  imageBase64Url: string,
  t?: (key: string) => string,
) {
  const typeParts = imageBase64Url.split(',')
  const type = typeParts[0]?.split(';')[0]?.split(':')[1]
  if (
    !type ||
    !['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(type)
  ) {
    throw new Error(
      t ? t('components.ocr.errors.invalidImageType') : 'Invalid image type',
    )
  }

  return [
    {
      role: 'user',
      content: [
        {
          type: 'text',
          text: getOCRPromptText(),
        },
        {
          type: 'image_url',
          image_url: {
            url: imageBase64Url,
          },
        },
      ],
    },
  ]
}

export function BusinessCardScanButton({
  children,
  tooltip,
  tooltipPosition = 'bottom',
  onValidResult,
  onStart,
  onError,
}: BusinessCardOcrProps) {
  const { t } = useTranslation()
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Check if device is Android or iOS
  const isAndroid = /Android/i.test(navigator.userAgent)
  const isSafari =
    /Safari/i.test(navigator.userAgent) && !/Chrome/i.test(navigator.userAgent)
  const isIOS = isSafari || /iPhone|iPad|iPod/i.test(navigator.userAgent)
  const isMobile = isAndroid || isIOS

  const handleImageFile = async (file: File) => {
    if (onStart) onStart()
    const base64ImageUrl = await getBase64(file)
    const result = await ocrCard(base64ImageUrl as string, t)
    if (result && onValidResult) {
      onValidResult(result)
    } else {
      if (onError) {
        onError(
          translate(
            t,
            'components.ocr.errors.parsingFailed',
            'Failed to parse contact information',
          ),
        )
      }
    }
  }

  const handleFileInputChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = e.target.files
    if (!files || !files.length) return

    const file = files[0] as File
    await handleImageFile(file)

    // Reset input if ref provided (for camera input)
    if (fileInputRef?.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleButtonClick = () => {
    fileInputRef.current?.click()
  }

  return (
    <>
      <button
        onClick={handleButtonClick}
        className="business-card-scan-button"
        /// not showing tooltip on iOS devices
        {...(tooltip && !isMobile ? { 'data-tooltip': tooltip } : {})}
        {...(tooltipPosition && !isMobile
          ? { 'data-flow': tooltipPosition }
          : {})}
      >
        {children}
      </button>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileInputChange}
        accept={'image/*' + (isAndroid ? ',.jpg,text/plain' : '')}
        hidden
      />
    </>
  )
}

const getBase64 = (file: File): Promise<string | ArrayBuffer | null> => {
  return new Promise((resolve) => {
    let baseURL: string | ArrayBuffer | null
    // Make new FileReader
    const reader = new FileReader()

    // Convert the file to base64 text
    reader.readAsDataURL(file)
    reader.onload = () => {
      baseURL = reader.result
      resolve(baseURL)
    }
  })
}
